import React, { useEffect, useState, useCallback, useRef } from "react";
import "./Favorites.scss";
import {
  theme,
  Layout,
  Spin,
  notification,
  Input,
  Pagination,
  Typography,
  Button,
} from "antd";
import { ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import useWindowWidth from "../../hooks/useWindowWidth";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getFavorites } from "../../features/favorites/favoritesActions";
import { resetFavoritesError } from "../../features/favorites/favoritesSlice";
import {
  CustomEmpty,
  ProductCard,
  ProductDrawer,
  ViewSwitch,
} from "../../components";
import useProductsView from "../../hooks/useProductsView";
import classNames from "classnames";
import Utils from "../../utils";

const { Content } = Layout;

const PER_PAGE = 25;

const doSearch = (value, cb) => {
  cb(value);
};
const debouncedSearch = Utils?.debounce(doSearch, 400);

export default function Favorites() {
  const { useToken } = theme;
  const { token } = useToken();
  const [api, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (type, title, text) => {
      api[type]({
        title: title,
        description: Utils.getErrorText(text),
        placement: "top",
      });
    },
    [api]
  );
  const { width, isMobile } = useWindowWidth();
  const { view, isList, onViewChange } = useProductsView();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loading, items: products, meta, error } = useSelector(
    (state) => state?.favorites
  );
  const dispatch = useDispatch();
  const productContRef = useRef(null);
  const [isDrawerOpened, setIsDrawerOpened] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchVal, setSearchVal] = useState(
    searchParams?.get("search") || ""
  );

  const setUrlSearchParams = (newParams) => {
    const filteredSearchParams = Utils?.get_filtered_url_params(searchParams, {
      page: searchParams?.get("page") ? searchParams?.get("page") : undefined,
      per_page: searchParams?.get("per_page")
        ? searchParams?.get("per_page")
        : undefined,
      search: searchParams?.get("search")
        ? searchParams?.get("search")
        : undefined,
      ...newParams,
    });
    // Replace instead of push: the params only exist so a reload or a shared
    // link restores the list. Pushing them would stack one history entry per
    // keystroke/page, and the back arrow would crawl through them instead of
    // leaving the page.
    setSearchParams(filteredSearchParams, { replace: true });
  };

  useEffect(() => {
    dispatch(
      getFavorites({
        page: searchParams?.get("page") || 1,
        per_page: searchParams?.get("per_page") || PER_PAGE,
        search: searchParams?.get("search") || undefined,
      })
    );
    setSearchVal(searchParams?.get("search") || "");
  }, [dispatch, searchParams]);

  const onSearchInpChange = (e) => {
    const value = e?.target?.value;
    setSearchVal(value);
    debouncedSearch(value, () => {
      setUrlSearchParams({
        page: 1,
        per_page: PER_PAGE,
        search: value ? value : undefined,
      });
    });
  };

  const handlePagination = (e, a) => {
    Utils?.scrollIntoViewByClassName(".favorites-cont");
    setUrlSearchParams({
      page: e,
      per_page: a,
    });
  };

  // A toggle on this page is always a removal (everything shown is a
  // favorite), so the visible list changed — refetch the current page. When
  // the last item of a page > 1 was removed, step back a page instead.
  const onFavoriteToggled = () => {
    const page = Number(searchParams?.get("page")) || 1;
    const wasLastItemOnPage = products?.length === 1 && page > 1;
    if (wasLastItemOnPage) {
      setUrlSearchParams({
        page: page - 1,
      });
    } else {
      dispatch(
        getFavorites({
          page,
          per_page: searchParams?.get("per_page") || PER_PAGE,
          search: searchParams?.get("search") || undefined,
        })
      );
    }
  };

  const openDrawer = (product) => {
    setIsDrawerOpened(true);
    setSelectedProduct(product);
  };

  useEffect(() => {
    if (location?.pathname === "/favorites") {
      if (error && !loading) {
        for (let key in error) {
          openNotificationWithIcon("error", key, error[key]);
        }
        dispatch(resetFavoritesError());
      }
    }
  }, [error, loading, location?.pathname, openNotificationWithIcon, dispatch]);

  return (
    <>
      {contextHolder}
      <Content
        className="favorites-cont"
        style={{
          backgroundColor: token?.Layout?.contentBg,
        }}>
        <div className="favorites-cont__searchWrapper">
          <div className="favorites-cont__searchWrapper__searchRow">
            <Button
              className="favorites-cont__searchWrapper__searchRow__backBtn"
              type="text"
              size="small"
              aria-label="Go back"
              icon={
                <ArrowLeftOutlined
                  style={{
                    fontSize: "24px",
                    color: token?.icons?.whiteIcon,
                  }}
                />
              }
              onClick={() => {
                navigate(-1);
              }}
            />
            <Typography.Text
              className="favorites-cont__searchWrapper__searchRow__title"
              style={{
                color: token?.Typography?.mainColorText,
              }}>
              My Favorites
            </Typography.Text>
          </div>
          <Input
            className="favorites-cont__searchWrapper__searchInp"
            size="large"
            placeholder="Search your favorites"
            prefix={<SearchOutlined />}
            allowClear
            onChange={onSearchInpChange}
            value={searchVal}
          />
          <div className="favorites-cont__searchWrapper__countRow">
            <div className="favorites-cont__searchWrapper__countWrapper">
              <Typography.Text
                className="favorites-cont__searchWrapper__countWrapper__text"
                style={{
                  color: token?.Typography?.colorPrimaryText,
                }}>
                {meta?.count ? meta?.count : 0}
              </Typography.Text>
              <Typography.Text
                className="favorites-cont__searchWrapper__countWrapper__text"
                style={{
                  color: token?.Typography?.mainColorText,
                }}>
                Favorites
              </Typography.Text>
            </div>
            <ViewSwitch
              view={view}
              onChange={onViewChange}
              size={isMobile ? "middle" : "large"}
            />
          </div>
        </div>
        <Spin
          size="large"
          spinning={loading}
          classNames={{
            root: classNames({
              "favorites-cont__products__spinWrapper":
                loading || !products?.length,
            }),
          }}>
          <div
            ref={productContRef}
            style={{
              gridTemplateColumns:
                !loading && !products?.length
                  ? `repeat(auto-fit, minmax(${"100%"},1fr))`
                  : isList
                  ? "minmax(0, 1fr)"
                  : `repeat(auto-fit, minmax(${isMobile ? "168px" : "179px"}, ${
                      isMobile
                        ? 350
                        : 250 / productContRef?.current?.offsetWidth
                    }fr))`,
            }}
            className={"favorites-cont__products"}>
            {!products?.length && !loading ? (
              <CustomEmpty
                circleColor={
                  token?.icons?.emptyCircleIconColor
                    ? token?.icons?.emptyCircleIconColor
                    : ""
                }
                boxBorderColor={
                  token?.icons?.emptyIconBoxBorderColor
                    ? token?.icons?.emptyIconBoxBorderColor
                    : ""
                }
                description={
                  searchParams?.get("search")
                    ? "Nothing matches your search"
                    : "No favorites yet"
                }
              />
            ) : (
              products?.map((product, index) => (
                <div
                  key={product?.id ?? `product-${product?.name}-${index}`}
                  style={{
                    height: "100%",
                    ...(product?.is_available === false
                      ? { opacity: 0.5 }
                      : null),
                  }}>
                  <ProductCard
                    product={product}
                    disabled={product?.is_available === false}
                    navigate={() => {
                      navigate(`/product/${product?.id}`, { state: product });
                      Utils?.scrollIntoViewByClassName(".appHeader-cont");
                    }}
                    haveDrawer={true}
                    openDrawer={openDrawer}
                    onFavoriteToggled={onFavoriteToggled}
                    layout={isList ? "list" : "grid"}
                  />
                </div>
              ))
            )}
          </div>
        </Spin>
        <Pagination
          className="favorites-cont__pagination"
          disabled={loading}
          pageSize={searchParams?.get("per_page") || PER_PAGE}
          current={searchParams?.get("page") || 1}
          defaultCurrent={searchParams?.get("page") || 1}
          total={meta?.count ? meta?.count : ""}
          responsive={true}
          onChange={handlePagination}
          showQuickJumper={width < 584 && width >= 577 ? false : true}
          showSizeChanger={false}
        />
        <ProductDrawer
          isDrawerOpened={isDrawerOpened}
          close={() => {
            setIsDrawerOpened(false);
            setTimeout(() => {
              setSelectedProduct(null);
            }, 300);
          }}
          product={selectedProduct}
        />
      </Content>
    </>
  );
}
